/*
  QB-02C — SBC-001 Commercial Document Standard v1.0 mappings.

  This module is presentation-only. It never modifies persisted data,
  pricing, GST calculations, backend schemas or the audit trail. It
  translates internal enum values into the approved client-facing labels
  required by SBC-001, and provides the four governed delivery methods.
*/

// SBC-001 §1.7 permitted lifecycle states (client-facing).
// Internal status enum → SBC-001 approved client label.
export const CLIENT_STATUS_LABEL_BY_INTERNAL = {
  DRAFT: "Draft",
  REQUIRES_CLARIFICATION: "Internal Review",
  READY_FOR_OWNER_REVIEW: "Internal Review",
  APPROVED: "Quotation",
  ISSUED: "Issued",
  ACCEPTED: "Issued",
  DECLINED: "Superseded",
  EXPIRED: "Archived",
  SUPERSEDED: "Superseded"
};

// SBC-001 §2.13 approved client-facing GST values.
export const CLIENT_GST_LABEL_BY_INTERNAL = {
  UNCONFIRMED: "GST Not Yet Confirmed",
  INCLUDED: "GST Included",
  EXCLUDED: "GST Excluded",
  NOT_APPLICABLE: "GST Not Yet Confirmed"
};

// SBC-001 §1.9 approved delivery methods (DM-001…DM-004).
export const DELIVERY_METHODS = [
  {
    code: "DM-001",
    label: "Document & Evidence Review",
    description:
      "Structured desktop review of the provider's documents and evidence pack. No live observation."
  },
  {
    code: "DM-002",
    label: "Remote Live Site Viewing",
    description:
      "Consultancy delivered by secure video link, including live observation of nominated operational practice. A professional consultancy delivery method — not regulatory approval, certification, formal audit or NDIA endorsement."
  },
  {
    code: "DM-003",
    label: "Physical On-site Consultancy",
    description:
      "Consultancy delivered on-site at a nominated provider location, including structured observation of operational practice within the agreed scope."
  },
  {
    code: "DM-004",
    label: "Hybrid Delivery",
    description:
      "A combination of desktop review, remote live site viewing and/or on-site consultancy as agreed within the scope."
  }
];

// Template → default delivery method mapping (SBC-001 §1.9 alignment).
// Used to pre-select a sensible delivery method when a template is
// applied; the consultant can still change it manually.
export const TEMPLATE_TO_DELIVERY_CODE = {
  remote_readiness_review: "DM-002",
  desktop_evidence_review: "DM-001",
  gap_analysis: "DM-001",
  policy_review: "DM-001",
  operational_readiness: "DM-004",
  on_site_consultancy: "DM-003",
  audit_preparation: "DM-004"
};

// Default delivery method when nothing else is available.
export const DEFAULT_DELIVERY_CODE = "DM-001";

export function getDeliveryMethodByCode(code) {
  return DELIVERY_METHODS.find((m) => m.code === code) || null;
}

// SBC-001 §12 Important Information — governing statement.
export const IMPORTANT_INFORMATION_STATEMENT = [
  "This engagement provides professional consultancy services.",
  "It does not constitute legal advice, regulatory approval, certification, registration approval or an approved external audit.",
  "Recommendations are based upon the information, documentation and observations made available during the engagement."
];

// SBC-001 §13 Acceptance wording.
export const ACCEPTANCE_STATEMENT = [
  "If you wish to proceed, please confirm your acceptance of this quotation.",
  "Work will commence following confirmation of acceptance and satisfaction of any agreed commencement requirements."
];

// SBC-001 §2.7.1 Purpose statement.
export const ENGAGEMENT_PURPOSE_STATEMENT =
  "This engagement has been designed to provide an independent professional consultancy service based on the agreed scope described within this document.";

// SBC-001 §2.7.2 typical inclusions (client-facing engagement overview).
export const ENGAGEMENT_TYPICAL_INCLUSIONS = [
  "Professional consultancy",
  "Evidence review",
  "Operational observations",
  "Structured discussions",
  "Practical recommendations",
  "Written deliverables"
];

// SBC-001 §2.7.3 typical exclusions (client-facing engagement overview).
export const ENGAGEMENT_TYPICAL_EXCLUSIONS = [
  "Legal advice",
  "Regulatory approval",
  "Audit certification",
  "Compliance guarantees",
  "Registration approval",
  "Representation before regulators"
];

// SBC-001 §2.7.4 How we work — collaborative approach.
export const ENGAGEMENT_HOW_WE_WORK =
  "Spiral Beyond Compliance works collaboratively with the provider. We review evidence, discuss operational practice, and provide practical written recommendations. Every recommendation remains the provider's decision to act upon.";

// SBC-001 §2.7.5 Expected outcomes — realistic intended outcomes.
export const ENGAGEMENT_EXPECTED_OUTCOMES =
  "A structured understanding of current readiness signals, a written record of findings within the agreed scope, and a prioritised set of next steps. No specific Commission, audit, funding, registration or compliance outcome is guaranteed.";
